'use client';

import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';

export interface PopoverApi<T extends HTMLElement> {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  toggle: () => void;
  close: () => void;
  /** Put this on the wrapper that contains both the trigger and the floating panel;
   *  a pointer-down outside it (or Escape) dismisses. */
  ref: RefObject<T | null>;
}

/**
 * The shared dropdown/popover **behaviour** (design system): open state plus dismiss
 * on outside-pointer and Escape, scoped to one wrapper ref. Every menu in the app -
 * `Select`, the party/group pickers, the profile menu, the views menu - uses this one
 * hook instead of re-implementing the same effect five times. Styling and content stay
 * the consumer's; only the behaviour is shared.
 */
export function usePopover<T extends HTMLElement = HTMLDivElement>(
  options: { onClose?: () => void } = {},
): PopoverApi<T> {
  const [open, setOpen] = useState(false);
  const ref = useRef<T>(null);
  const onCloseRef = useRef(options.onClose);
  useEffect(() => {
    onCloseRef.current = options.onClose;
  });

  useEffect(() => {
    if (!open) return;
    const dismiss = () => {
      setOpen(false);
      onCloseRef.current?.();
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // A detached target means the pressed element was re-rendered away by a
      // handler that ran before this document listener (React delegates at the
      // root, so a consumer's onMouseDown flushes first - e.g. picking a
      // suggestion re-keys the listbox). That press originated INSIDE something
      // we rendered; `contains` on a detached node would misread it as outside
      // and dismiss the popover mid-interaction.
      if (!target.isConnected) return;
      if (ref.current && !ref.current.contains(target)) dismiss();
    };
    // While open the wrapper wears `data-popover-open`, so whatever else
    // listens for Esc can see that a popover owns it: the peek defers to it
    // (src/components/modal.tsx), and so does a popover holding another one,
    // which leaves the first Esc to the inner. An Esc a control inside already
    // handled (`preventDefault`, as a Select does when it closes its list)
    // closes nothing more: React's handler runs before this document listener
    // and has already unmounted the inner popover by then.
    const el = ref.current;
    el?.setAttribute('data-popover-open', '');
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (ref.current?.querySelector('[data-popover-open]')) return;
      dismiss();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      el?.removeAttribute('data-popover-open');
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return {
    open,
    setOpen,
    toggle: () => setOpen((o) => !o),
    close: () => setOpen(false),
    ref,
  };
}
